/**
 * Life4Earth V2 — Project type dispatcher (DEV-810 / DEV-901 stage 3a).
 *
 * Mounted at `/projects/:id/:tab?`. Skip's Q1 decision (2026-05-19): the
 * canonical user-facing URL is shared between Full and Mini Projects. The
 * dispatcher decides which page component to render based on the GUID.
 *
 * Strategy:
 *   1. Try GET /api/mini-projects/{guid} — uses useApiMiniProject which
 *      returns null on 404 (no exception thrown).
 *   2. If the response is a Mini-Project, render MiniProjectDetailPage.
 *   3. Otherwise fall through to ProjectDetailPage (existing Full Project
 *      page) which handles the Full Project GUID space.
 *
 * Future optimization: when the Mini-Project list is already loaded
 * in App.tsx state (stage 3f portfolio integration), we can decide the
 * type synchronously without a second API call. For stage 3a the
 * round-trip is acceptable.
 */

import { useParams } from "react-router-dom";
import { useApiMiniProject } from "../../api/useApiMiniProject";
import MiniProjectDetailPage from "../MiniProjectDetail/MiniProjectDetailPage";
import ProjectDetailPage from "../ProjectDetail/ProjectDetailPage";

interface Props {
  currentUserGuid: string;
  debugMode: boolean;
  onProjectConfigChanged: () => void;
  projectData: any;
}

export default function ProjectDispatcher(props: Props) {
  const { id } = useParams<{ id: string; tab?: string }>();
  const { miniProject, loading, notFound, error } = useApiMiniProject(id);

  if (loading) {
    return <div style={{ padding: 48, textAlign: "center", color: "var(--text-muted)" }}>Loading…</div>;
  }

  // Hard error talking to the Mini-Project API (not just 404). Fall through
  // to Full Project rather than blocking — Full Project page may still work
  // even if Mini-Project API is down.
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("[ProjectDispatcher] Mini-Project lookup failed; falling through to Full Project page:", error);
    return <ProjectDetailPage {...props} />;
  }

  if (miniProject && !notFound) {
    return <MiniProjectDetailPage currentUserGuid={props.currentUserGuid} />;
  }

  // 404 from Mini-Project API → guid is presumably a Full Project. Hand off.
  return <ProjectDetailPage {...props} />;
}
